import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Pagination from '@mui/material/Pagination';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Layout from './Layout';
import { useAuth } from '../context/AuthContext';
import './PickListList.css';
import './CustomersList.css';

const PAGE_SIZE = 50;

function sameId(a, b) {
  if (a == null || b == null) return false;
  return String(a) === String(b);
}

function formatCustomerDate(d) {
  if (!d) return '—';
  const x = new Date(d);
  return Number.isNaN(x.getTime()) ? String(d) : x.toLocaleString();
}

function formatStatusLabel(s) {
  const x = (s || 'pending').toLowerCase();
  return x.charAt(0).toUpperCase() + x.slice(1);
}

const CustomersList = () => {
  const navigate = useNavigate();
  const { fetchWithAuth } = useAuth();

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [listLoading, setListLoading] = useState(true);
  const [error, setError] = useState('');

  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const prevTrimmedSearchRef = useRef(null);

  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [detailTab, setDetailTab] = useState(0);

  const [pickLists, setPickLists] = useState([]);
  const [pickListsLoading, setPickListsLoading] = useState(false);

  const [stoConsignments, setStoConsignments] = useState([]);
  const [stoLoading, setStoLoading] = useState(false);

  const [notesDraft, setNotesDraft] = useState('');
  const [notesSaving, setNotesSaving] = useState(false);

  const selectedCustomerId = selectedCustomer?.id;
  const selectedCustomerNotes = selectedCustomer?.notes ?? '';

  const mergeCustomerIntoState = useCallback((updated) => {
    setSelectedCustomer(updated);
    setItems((prev) => prev.map((r) => (sameId(r.id, updated.id) ? { ...r, ...updated } : r)));
  }, []);

  useEffect(() => {
    const t = window.setTimeout(() => {
      const next = searchInput.trim();
      if (prevTrimmedSearchRef.current !== next) {
        prevTrimmedSearchRef.current = next;
        setDebouncedSearch(next);
        setPage(1);
      }
    }, 400);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const loadList = useCallback(async () => {
    try {
      setListLoading(true);
      setError('');
      const params = new URLSearchParams({
        page: String(page),
        limit: String(PAGE_SIZE),
        q: debouncedSearch,
      });
      const res = await fetchWithAuth(`/api/customers?${params.toString()}`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Failed to load customers');
      setItems(Array.isArray(data.items) ? data.items : []);
      setTotal(Number(data.total) || 0);
    } catch (err) {
      setError(err.message || 'Failed to load customers');
      setItems([]);
      setTotal(0);
    } finally {
      setListLoading(false);
    }
  }, [fetchWithAuth, page, debouncedSearch]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    setPickLists([]);
    setStoConsignments([]);
  }, [selectedCustomerId]);

  useEffect(() => {
    setNotesDraft(selectedCustomerNotes);
  }, [selectedCustomerId, selectedCustomerNotes]);

  useEffect(() => {
    if (!selectedCustomerId || detailTab !== 1) return;
    let cancelled = false;
    (async () => {
      setPickListsLoading(true);
      try {
        const res = await fetchWithAuth(`/api/customers/${selectedCustomerId}/pick-lists`);
        const data = await res.json().catch(() => []);
        if (!cancelled) setPickLists(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setPickLists([]);
      } finally {
        if (!cancelled) setPickListsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedCustomerId, detailTab, fetchWithAuth]);

  useEffect(() => {
    if (!selectedCustomerId || detailTab !== 2) return;
    let cancelled = false;
    (async () => {
      setStoLoading(true);
      try {
        const res = await fetchWithAuth(`/api/customers/${selectedCustomerId}/sto-consignments?limit=100`);
        const data = await res.json().catch(() => []);
        if (!cancelled) setStoConsignments(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setStoConsignments([]);
      } finally {
        if (!cancelled) setStoLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedCustomerId, detailTab, fetchWithAuth]);

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const notesDirty = selectedCustomer && notesDraft !== (selectedCustomer.notes || '');

  const handleSaveNotes = async () => {
    if (!selectedCustomer || notesSaving) return;
    setNotesSaving(true);
    try {
      const res = await fetchWithAuth(`/api/customers/${selectedCustomer.id}/notes`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes: notesDraft }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Failed to save notes');
      mergeCustomerIntoState(data);
      setNotesDraft(data.notes || '');
    } catch (e) {
      window.alert(e.message || 'Failed to save notes');
    } finally {
      setNotesSaving(false);
    }
  };

  const handleRowClick = (row) => {
    setSelectedCustomer(row);
    setDetailTab(0);
  };

  return (
    <Layout>
      <Box className="pick-list-md-page" sx={{ p: 2 }}>
        <div className="pick-list-md-intro">
          <h1 className="home-menu-title">Customer List</h1>
        </div>

        {error && (
          <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <div className="pick-list-md-toolbar">
          <Button
            type="button"
            variant="text"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/admin/customers')}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Back
          </Button>
          <Divider orientation="vertical" flexItem />
          <Button
            type="button"
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => {
              loadList();
            }}
            disabled={listLoading}
            sx={{ textTransform: 'none' }}
          >
            Refresh
          </Button>
        </div>

        <div className="pick-list-md-split">
          <div className="pick-list-md-master">
            <div className="pick-list-md-section-header">Customers</div>
            <div className="pick-list-md-search">
              <TextField
                size="small"
                fullWidth
                label="Search"
                placeholder="Cust no, category, name…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                margin="dense"
                sx={{ mt: 0 }}
              />
              <div className="customers-page-hint">
                {total > 0 && (
                  <span>
                    Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
                  </span>
                )}
              </div>
            </div>
            <div className="pick-list-md-list-scroll">
              {listLoading ? (
                <p className="pick-list-md-muted">Loading…</p>
              ) : items.length === 0 ? (
                <p className="pick-list-md-muted">
                  {total === 0 && !debouncedSearch ? 'No customers yet. Import from Customers → Import.' : 'No matches.'}
                </p>
              ) : (
                <table className="pick-list-md-table">
                  <thead>
                    <tr>
                      <th>CUST NO</th>
                      <th>CAT</th>
                      <th>CUSTOMER NAME</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((r) => (
                      <tr
                        key={r.id}
                        className={sameId(selectedCustomer?.id, r.id) ? 'pick-list-md-row-selected' : ''}
                        onClick={() => handleRowClick(r)}
                      >
                        <td>{r.custNo}</td>
                        <td>{r.category || '—'}</td>
                        <td className="customers-name-cell" title={r.customerName}>
                          {r.customerName}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            {pageCount > 1 && (
              <div className="customers-pagination">
                <Pagination
                  count={pageCount}
                  page={page}
                  onChange={(_, p) => setPage(p)}
                  size="small"
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </div>
            )}
          </div>

          <div className="pick-list-md-detail">
            <div className="pick-list-md-section-header">
              {selectedCustomer
                ? `${selectedCustomer.customerName || 'Customer'} · ${selectedCustomer.custNo}`
                : 'Customer details'}
            </div>
            {!selectedCustomer ? (
              <div className="pick-list-md-detail-empty">Select a customer from the list.</div>
            ) : (
              <>
                <Tabs
                  value={detailTab}
                  onChange={(_, v) => setDetailTab(v)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{ borderBottom: 1, borderColor: 'divider', px: 1, minHeight: 42 }}
                >
                  <Tab label="Overview" sx={{ textTransform: 'none', minHeight: 42 }} />
                  <Tab
                    label={
                      detailTab === 1 && pickListsLoading
                        ? 'Pick lists (…)'
                        : `Pick lists (${pickLists.length})`
                    }
                    sx={{ textTransform: 'none', minHeight: 42 }}
                  />
                  <Tab
                    label={
                      detailTab === 2 && stoLoading ? 'Related STOs (…)' : `Related STOs (${stoConsignments.length})`
                    }
                    sx={{ textTransform: 'none', minHeight: 42 }}
                  />
                  <Tab label="Notes" sx={{ textTransform: 'none', minHeight: 42 }} />
                  <Tab label="Identifiers" sx={{ textTransform: 'none', minHeight: 42 }} />
                </Tabs>
                <div className="pick-list-md-detail-body">
                  {detailTab === 0 && (
                    <div className="pick-list-overview-grid">
                      <div className="pick-list-overview-item">
                        <span className="pick-list-overview-label">Customer no.</span>
                        <span className="pick-list-overview-value">{selectedCustomer.custNo}</span>
                      </div>
                      <div className="pick-list-overview-item">
                        <span className="pick-list-overview-label">Category</span>
                        <span className="pick-list-overview-value">{selectedCustomer.category || '—'}</span>
                      </div>
                      <div className="pick-list-overview-item pick-list-overview-item--wide">
                        <span className="pick-list-overview-label">Customer name</span>
                        <span className="pick-list-overview-value">{selectedCustomer.customerName}</span>
                      </div>
                      <div className="pick-list-overview-item">
                        <span className="pick-list-overview-label">Created</span>
                        <span className="pick-list-overview-value">{formatCustomerDate(selectedCustomer.createdAt)}</span>
                      </div>
                      <div className="pick-list-overview-item">
                        <span className="pick-list-overview-label">Updated</span>
                        <span className="pick-list-overview-value">{formatCustomerDate(selectedCustomer.updatedAt)}</span>
                      </div>
                    </div>
                  )}
                  {detailTab === 1 && (
                    <>
                      {pickListsLoading ? (
                        <p className="pick-list-md-muted">Loading pick lists…</p>
                      ) : pickLists.length === 0 ? (
                        <p className="pick-list-md-muted">No pick lists found for this customer number.</p>
                      ) : (
                        <div className="pick-list-lines-wrap">
                          <table className="pick-list-lines-table">
                            <thead>
                              <tr>
                                <th>ID</th>
                                <th>Order</th>
                                <th>OBD</th>
                                <th>Status</th>
                                <th>Created</th>
                              </tr>
                            </thead>
                            <tbody>
                              {pickLists.map((pl) => (
                                <tr key={pl.id}>
                                  <td>{pl.id}</td>
                                  <td>{pl.orderNumber || '—'}</td>
                                  <td>{pl.obdNumber || '—'}</td>
                                  <td>
                                    <span className={`pick-list-status-badge pick-list-status-${(pl.status || 'pending').toLowerCase()}`}>
                                      {formatStatusLabel(pl.status)}
                                    </span>
                                  </td>
                                  <td>{formatCustomerDate(pl.createdAt)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </>
                  )}
                  {detailTab === 2 && (
                    <>
                      {stoLoading ? (
                        <p className="pick-list-md-muted">Loading STO consignments…</p>
                      ) : stoConsignments.length === 0 ? (
                        <p className="pick-list-md-muted">No related STO consignments found for this customer number or name in document headers or line items.</p>
                      ) : (
                        <div className="pick-list-lines-wrap">
                          <table className="pick-list-lines-table">
                            <thead>
                              <tr>
                                <th>File</th>
                                <th>Document header</th>
                                <th>Posting</th>
                                <th>Status</th>
                                <th>Consignment</th>
                              </tr>
                            </thead>
                            <tbody>
                              {stoConsignments.map((row) => (
                                <tr key={row.consignmentId}>
                                  <td className="customers-name-cell" title={row.fileName}>
                                    {row.fileName || '—'}
                                  </td>
                                  <td className="customers-name-cell" title={row.documentHeaderText}>
                                    {row.documentHeaderText || '—'}
                                  </td>
                                  <td>{row.postingDate || '—'}</td>
                                  <td>
                                    <span
                                      className={`pick-list-status-badge pick-list-status-${(row.status || 'pending').toLowerCase()}`}
                                    >
                                      {formatStatusLabel(row.status)}
                                    </span>
                                  </td>
                                  <td className="customers-mono-cell">{row.consignmentId}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                      <p className="customers-sto-hint">
                        Rows match when the customer number appears anywhere in the STO header or serialized line items, or when the customer name (4+ characters) appears in the document header.
                      </p>
                    </>
                  )}
                  {detailTab === 3 && (
                    <Stack spacing={2} className="customers-notes-panel">
                      <TextField
                        label="Internal notes"
                        placeholder="Notes visible to admins on this customer record…"
                        multiline
                        minRows={8}
                        fullWidth
                        value={notesDraft}
                        onChange={(e) => setNotesDraft(e.target.value)}
                        size="small"
                      />
                      <Button
                        variant="contained"
                        onClick={handleSaveNotes}
                        disabled={notesSaving || !notesDirty}
                        sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
                      >
                        {notesSaving ? 'Saving…' : 'Save notes'}
                      </Button>
                    </Stack>
                  )}
                  {detailTab === 4 && (
                    <div className="pick-list-overview-grid">
                      <div className="pick-list-overview-item">
                        <span className="pick-list-overview-label">Record ID</span>
                        <span className="pick-list-overview-value">{selectedCustomer.id}</span>
                      </div>
                      <div className="pick-list-overview-item pick-list-overview-item--wide">
                        <span className="pick-list-overview-label">Cust no. (import key)</span>
                        <span className="pick-list-overview-value">{selectedCustomer.custNo}</span>
                      </div>
                      <p className="customers-identifiers-note">
                        Pick lists are matched to customers using this customer number and the customer field on imported pick lists.
                      </p>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </Box>
    </Layout>
  );
};

export default CustomersList;
